import { useCallback, useEffect, useReducer } from 'react';
import { AbortError } from '../../utils/utils';

export type State<T> = {
  data: T | null;
  error: Error | null;
  loading: boolean;
};

type Action<T> =
  | {
      type: 'resolved';
      data: T;
    }
  | {
      type: 'rejected';
      error: Error;
    }
  | { type: 'pending' };

function reducer<T>(state: State<T>, action: Action<T>): State<T> {
  switch (action.type) {
    case 'resolved':
      return {
        ...state,
        data: action.data,
        error: null,
        loading: false
      };

    case 'rejected':
      return {
        ...state,
        data: null,
        error: action.error,
        loading: false
      };

    case 'pending':
      return {
        ...state,
        error: null,
        data: null,
        loading: true
      };

    default:
      throw new Error();
  }
}

function usePromise<T>(
  promise: () => Promise<T>,
  dependencies: Array<any>,
  signal?: AbortSignal | null,
  onCancel?: () => void
) {
  const [state, dispatch] = useReducer(reducer, {
    data: null,
    error: null,
    loading: false
  });

  const memoizedPromise = useCallback(promise, dependencies); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let unmounted = false;
    let aborted = false;

    function abort() {
      aborted = true;
      dispatch({ type: 'rejected', error: new AbortError() });
    }

    if (signal) {
      if (signal.aborted) {
        throw new AbortError();
      }

      signal.addEventListener('abort', abort);
    }

    (async () => {
      try {
        if (!unmounted && !aborted) {
          dispatch({ type: 'pending' });
        }
        const value = await memoizedPromise();
        if (!unmounted && !aborted) {
          dispatch({ type: 'resolved', data: value });
        }
      } catch (error: any) {
        if (!unmounted && !aborted) {
          if (error instanceof Error) {
            dispatch({ type: 'rejected', error });
          }
        }
      } finally {
        if (signal) {
          signal.removeEventListener('abort', abort);
        }
      }
    })();

    return () => {
      unmounted = true;
      if (signal) {
        signal.removeEventListener('abort', abort);
      }
      if (onCancel) {
        onCancel();
      }
    };
  }, [memoizedPromise, onCancel, signal]);

  return state;
}

export default usePromise;
